import { useEffect, useMemo, useRef, useState } from 'react'
import { TransitionLoader } from '@opallabs/kuiper'
import { useOnce } from '@opallabs/lambda'
import { TASK_GLYPH } from '@opallabs/lapidary'
import { TaskItem } from '@opallabs/satellite'
import { AssignmentStatus } from '@opallabs/satellite/src/AssignmentHelpers/types'
import { EmptyState } from '../../Shared/EmptyState'
import * as sharedStyles from '../../Shared/styles'
import { SubPanelProps } from './types'

export const UpcomingPanel: React.FC<SubPanelProps> = ({
  createCheckpoint,
  getTasks,
  isLoading,
  onTaskCheckboxClick,
  onTaskStarClick,
  setTaskPreviewCard,
  tasks,
  theme = 'light',
}) => {
  const checkpointCreated = useRef(false)

  const [activeTask, setActiveTask] = useState<string | number>(
    tasks.length ? tasks[0].id : -1
  )

  const isEmpty = useMemo(() => tasks.length === 0, [tasks])

  useEffect(() => {
    if (!checkpointCreated.current && !isLoading) {
      checkpointCreated.current = true
      createCheckpoint()
    }
  }, [checkpointCreated, createCheckpoint, isLoading])

  useOnce(getTasks)

  if (!isLoading && isEmpty) {
    return (
      <EmptyState
        icon={TASK_GLYPH}
        text="You don’t have any upcoming tasks right now"
        theme={theme}
      />
    )
  }

  return (
    <TransitionLoader
      loaded={!isLoading}
      minHeight={600} // this is an arbitrary size that looks good
      theme="transparent"
    >
      <div>
        {tasks.filter((task) => task.starred).length > 0 && (
          <h3 css={sharedStyles.title({ theme })}>Starred Upcoming Tasks</h3>
        )}
        {tasks
          .filter((task) => task.starred)
          .map((task) => (
            <TaskItem
              {...task}
              taskStatus={
                activeTask === task.id
                  ? AssignmentStatus.Active
                  : task.taskStatus
              }
              key={`task-upcoming-starred-${task.id}`}
              onItemClick={(): void => {
                setActiveTask(task.id)
                setTaskPreviewCard(task)
              }}
              onCheckboxClick={(): void => onTaskCheckboxClick(task)}
              onStarClick={(): void => onTaskStarClick(task)}
            />
          ))}
        {tasks.filter((task) => !task.starred).length > 0 && (
          <h3 css={sharedStyles.title({ theme })}>Upcoming Tasks</h3>
        )}
        {tasks
          .filter((task) => !task.starred)
          .map((task) => (
            <TaskItem
              {...task}
              taskStatus={
                activeTask === task.id
                  ? AssignmentStatus.Active
                  : task.taskStatus
              }
              key={`task-upcoming-${task.id}`}
              onItemClick={(): void => {
                setActiveTask(task.id)
                setTaskPreviewCard(task)
              }}
              onCheckboxClick={(): void => onTaskCheckboxClick(task)}
              onStarClick={(): void => onTaskStarClick(task)}
            />
          ))}
      </div>
    </TransitionLoader>
  )
}
